import { Button } from '@crowdex/ui/components/button';
import { Input } from '@crowdex/ui/components/input';
import { cn } from '@crowdex/ui/lib/utils';
import type { ComponentProps, ReactNode } from 'react';

/*
 * Floating browser toolbar pieces, shared by browser tabs and the empty
 * "New tab": pill-shaped groups on the panel surface instead of a bordered bar.
 */

export function BrowserToolbar({ children }: { children: ReactNode }) {
  return <div className="flex shrink-0 items-center gap-2 px-2.5 py-2">{children}</div>;
}

/** A floating group of toolbar controls. */
export function ToolbarPill({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        'flex h-8 shrink-0 items-center rounded-control border border-hairline bg-surface-raised px-0.5',
        className,
      )}
    >
      {children}
    </div>
  );
}

export function ToolbarButton({ className, ...props }: ComponentProps<typeof Button>) {
  return (
    <Button
      className={cn(
        'size-7 rounded-control text-muted-foreground hover:text-foreground',
        className,
      )}
      size="icon-sm"
      type="button"
      variant="ghost"
      {...props}
    />
  );
}

/** The pill address field: centered at rest, left-aligned while editing, submits on Enter. */
export function BrowserAddressField({
  onSubmit,
  ...props
}: Omit<ComponentProps<typeof Input>, 'onSubmit'> & { onSubmit: () => void }) {
  return (
    <Input
      className="h-8 min-w-0 flex-1 rounded-control border-hairline bg-surface-raised px-4 text-center text-xs shadow-none focus-visible:text-left"
      onFocus={(event) => event.currentTarget.select()}
      onKeyDown={(event) => {
        if (event.key === 'Enter') onSubmit();
      }}
      placeholder="Search or enter a URL"
      {...props}
    />
  );
}
